import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { toast } from "sonner";

const INTENSITY_METRICS = [
  { value: "flavor", label: "Flavor" },
  { value: "color", label: "Color" },
  { value: "aroma", label: "Aroma" },
  { value: "heat", label: "Heat" },
  { value: "pungency", label: "Pungency" },
  { value: "sweetness", label: "Sweetness" },
  { value: "earthiness", label: "Earthiness" },
];

const INTENSITY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const SPICE_CATEGORIES = ["powder", "whole", "blend", "masala", "herbs", "seasoning"];

const emptyVariant = {
  size: "",
  mrp: "",
  discount_percent: 0,
  sku: "",
  in_stock: true,
  is_active: true,
};

const defaultProduct = {
  name: "",
  slug: "",
  category: "",
  description: "",
  intensity_metric: "flavor",
  intensity_level: "medium",
  intensity_notes: "",
  benefits: [],
  image_urls: [],
  is_active: true,
};

const AddProduct = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(defaultProduct);
  const [variants, setVariants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: formData }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || "Image upload failed");

      setProduct((prev) => ({
        ...prev,
        image_urls: [...prev.image_urls, data.secure_url],
      }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addVariant = () => setVariants((prev) => [...prev, { ...emptyVariant }]);

  const updateVariant = (index, field, value) => {
    setVariants((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeVariant = (index) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  const totalPotentialSkus = useMemo(
    () => variants.filter((v) => v.sku?.trim()).length,
    [variants]
  );

  const saveProduct = async () => {
    if (!product.name || !product.category) {
      toast.error("Name and category are required");
      return;
    }
    if (variants.length === 0) {
      toast.error("Add at least one variant");
      return;
    }

    setSaving(true);
    try {
      const productPayload = {
        ...product,
        slug: product.slug || generateSlug(product.name),
      };

      const productRes = await api.post("/admin/products", productPayload);
      const productId = productRes.data.id;

      for (const v of variants) {
        if (!v.size || !v.sku || !v.mrp) {
          throw new Error("Each variant must have size, MRP and SKU");
        }
        const mrp = Number(v.mrp);
        const discount = Number(v.discount_percent || 0);
        const sellingPrice = mrp - (mrp * discount) / 100;

        await api.post("/admin/variants", {
          product_id: productId,
          size: v.size,
          mrp,
          discount_percent: discount,
          selling_price: Number(sellingPrice.toFixed(2)),
          sku: v.sku,
          in_stock: v.in_stock,
          is_active: v.is_active,
        });
      }

      toast.success("Product created successfully");
      navigate(`/admin/products/${productId}/edit`);
    } catch (err) {
      const message = err?.message || "Failed to create product";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <button
              onClick={() => navigate("/admin/products")}
              className="text-sm text-amber-700 hover:text-amber-800"
            >
              ← Back to Products
            </button>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">Add New Product</h1>
            <p className="text-sm text-gray-600 mt-1">
              Create product profile, assign spice intensity and configure variants in one flow.
            </p>
          </div>
          <button
            disabled={saving}
            onClick={saveProduct}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
          >
            {saving ? "Creating..." : "Create Product"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Product Information</h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              className="input w-full"
              placeholder="Enter product name"
              value={product.name}
              onChange={(e) =>
                setProduct((prev) => ({
                  ...prev,
                  name: e.target.value,
                  slug: generateSlug(e.target.value),
                }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              className="input w-full"
              list="spice-category-options"
              placeholder="Category (e.g. powder, blend)"
              value={product.category}
              onChange={(e) => setProduct((prev) => ({ ...prev, category: e.target.value }))}
            />
            <datalist id="spice-category-options">
              {SPICE_CATEGORIES.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Metric</label>
            <select
              className="input w-full"
              value={product.intensity_metric}
              onChange={(e) => setProduct((prev) => ({ ...prev, intensity_metric: e.target.value }))}
            >
              {INTENSITY_METRICS.map((metric) => (
                <option key={metric.value} value={metric.value}>
                  {metric.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Level</label>
            <select
              className="input w-full"
              value={product.intensity_level}
              onChange={(e) => setProduct((prev) => ({ ...prev, intensity_level: e.target.value }))}
            >
              {INTENSITY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            className="input w-full min-h-[120px]"
            placeholder="Add product description"
            value={product.description}
            onChange={(e) => setProduct((prev) => ({ ...prev, description: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Intensity Notes (optional)</label>
          <textarea
            className="input w-full min-h-[90px]"
            placeholder="Example: Rich yellow color and strong visual depth in curries."
            value={product.intensity_notes}
            onChange={(e) => setProduct((prev) => ({ ...prev, intensity_notes: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
          <input
            className="input w-full"
            placeholder="Comma separated benefits"
            value={product.benefits.join(", ")}
            onChange={(e) =>
              setProduct((prev) => ({
                ...prev,
                benefits: e.target.value
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean),
              }))
            }
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Product Images</h2>
        <div className="flex flex-wrap gap-3">
          {product.image_urls.map((img, i) => (
            <img
              key={`${img}-${i}`}
              src={img}
              alt=""
              className="h-24 w-24 object-cover rounded-lg border border-gray-200"
            />
          ))}
        </div>
        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files?.[0])} />
        {uploading && <p className="text-sm text-gray-600">Uploading image...</p>}
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Variants</h2>
          <div className="flex items-center gap-3">
            <p className="text-sm text-gray-500">{variants.length} variant(s)</p>
            <button
              onClick={addVariant}
              className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
            >
              + Add Variant
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {variants.map((variant, index) => {
            const mrp = Number(variant.mrp || 0);
            const discount = Number(variant.discount_percent || 0);
            const selling = mrp - (mrp * discount) / 100;

            return (
              <div key={index} className="rounded-xl border border-gray-200 p-4 bg-gray-50/70">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Variant {index + 1}</h3>
                  <button
                    onClick={() => removeVariant(index)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Size</label>
                    <input
                      className="input w-full"
                      placeholder="100g"
                      value={variant.size}
                      onChange={(e) => updateVariant(index, "size", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">MRP</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={variant.mrp}
                      onChange={(e) => updateVariant(index, "mrp", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Discount %</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={variant.discount_percent}
                      onChange={(e) => updateVariant(index, "discount_percent", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Selling Price</label>
                    <input
                      className="input w-full bg-white"
                      disabled
                      value={mrp ? selling.toFixed(2) : ""}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3 mt-3">
                  <div className="md:col-span-1">
                    <label className="block text-xs text-gray-600 mb-1">SKU</label>
                    <input
                      className="input w-full"
                      placeholder="SKU-123"
                      value={variant.sku}
                      onChange={(e) => updateVariant(index, "sku", e.target.value)}
                    />
                  </div>
                  <label className="text-xs text-gray-600 flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={variant.in_stock}
                      onChange={() => updateVariant(index, "in_stock", !variant.in_stock)}
                    />
                    In Stock
                  </label>
                  <label className="text-xs text-gray-600 flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={variant.is_active}
                      onChange={() => updateVariant(index, "is_active", !variant.is_active)}
                    />
                    Enabled
                  </label>
                </div>
              </div>
            );
          })}

          {variants.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Add at least one variant to create this product.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-gray-700 font-medium">Ready to create product?</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {variants.length} variant(s) configured, {totalPotentialSkus} SKU(s) filled.
            </p>
          </div>
          <button
            disabled={saving}
            onClick={saveProduct}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
          >
            {saving ? "Creating..." : "Create Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
